import { Injectable, Signal, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Client, IMessage, ReconnectionTimeMode, StompSubscription } from '@stomp/stompjs';
import { Observable, Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import { StompAccessTokenService } from './stomp-access-token.service';
import { stompBrokerUrl, withBearerHeader } from './stomp-broker';
import { LivePipelineEntry } from '../models/live-pipeline.model';
import { LiveFeedConnection } from '../models/live-feed-connection.model';

/** Ein fortgeschriebener Eintrag mit dem Topic, ueber das er kam. */
export interface LivePipelineFeedEvent {
  projectId: string;
  entry: LivePipelineEntry;
}

const RECONNECT_DELAY_MS = 1_000;
const MAX_RECONNECT_DELAY_MS = 30_000;

/**
 * Liste und Live-Fortschreibung der laufenden Pipeline-Durchlaeufe (NEX-1232).
 *
 * <p>Eine Verbindung mit einem Abonnement je Projekt: die Sektion zeigt die
 * Pipelines aller Projekte des Nutzers nebeneinander, und der Broker adressiert
 * je Projekt (`LivePipelineFeedPublisher.topicFor`).
 *
 * <p>Ein Dienst je Ansicht statt einer wurzelweiten Instanz — wie
 * {@code TestlaufLiveFeedService}: schaltet die Person die Sektion ab, faellt die
 * Verbindung mit ihr. Den Wiederaufbau uebernimmt der STOMP-Client selbst; eine
 * eigene Retry-Schleife daneben wuerde dieselbe Verbindung abwechselnd aufbauen
 * und abreissen. Was die Sektion davon braucht, ist {@link connection} (NEX-1307).
 */
@Injectable()
export class LivePipelineService {
  private readonly http = inject(HttpClient);
  private readonly accessToken = inject(StompAccessTokenService);

  /** Ueberschreibbarer Seam fuer Tests — echte Verbindung nur im Betrieb. */
  clientFactory: (brokerUrl: string) => Client = (brokerUrl) =>
    new Client({
      brokerURL: brokerUrl,
      reconnectDelay: RECONNECT_DELAY_MS,
      maxReconnectDelay: MAX_RECONNECT_DELAY_MS,
      reconnectTimeMode: ReconnectionTimeMode.EXPONENTIAL,
    });

  private readonly connectionState = signal<LiveFeedConnection>('connecting');
  readonly connection: Signal<LiveFeedConnection> = this.connectionState.asReadonly();

  private readonly eventSubject = new Subject<LivePipelineFeedEvent>();
  readonly events: Observable<LivePipelineFeedEvent> = this.eventSubject.asObservable();

  /**
   * Meldet jeden Wiederaufbau ab dem zweiten Connect. Was der Broker in der Luecke
   * zugestellt haette, ist verloren — auch das Ende eines Durchlaufs —, die Ansicht
   * muss ihren Stand deshalb einmal per REST abgleichen. Der erste Connect zaehlt
   * nicht, auch nicht nach einem gescheiterten ersten Versuch: ohne vorherige
   * Verbindung gibt es keine Luecke, der gerade geladene Stand ist aktuell.
   */
  private readonly reconnectSubject = new Subject<void>();
  readonly reconnected: Observable<void> = this.reconnectSubject.asObservable();

  private client: Client | null = null;
  private subscriptions: StompSubscription[] = [];

  list(projectId: string): Observable<LivePipelineEntry[]> {
    return this.http.get<LivePipelineEntry[]>(`${environment.apiBaseUrl}/api/v1/projects/${projectId}/pipelines/live`);
  }

  connect(projectIds: string[]): void {
    if (this.client || projectIds.length === 0) {
      return;
    }
    this.connectionState.set('connecting');
    const client = this.clientFactory(stompBrokerUrl());
    withBearerHeader(client, () => this.accessToken.acquire());
    // Die Abonnements haengen am Verbindungsaufbau und nicht an `activate`: nach
    // jedem Reconnect sind die alten Abonnements des Brokers fort, und ohne neue
    // bliebe die Sektion still verbunden, ohne je wieder etwas zu empfangen.
    let connectedBefore = false;
    client.onConnect = () => {
      this.connectionState.set('connected');
      if (connectedBefore && this.client === client) {
        this.reconnectSubject.next();
      }
      connectedBefore = true;
      this.subscriptions = projectIds.map((projectId) =>
        client.subscribe(topicFor(projectId), (message: IMessage) => this.dispatch(projectId, message)),
      );
    };
    // Das Schliessen kommt nach `deactivate` erst spaeter an. Ein Client, den die
    // Ansicht schon abgegeben hat, meldet deshalb keinen Verlust mehr — weder fuer
    // sich noch fuer die Verbindung, die an seine Stelle getreten ist.
    client.onWebSocketClose = () => {
      if (this.client === client) {
        this.subscriptions = [];
        this.connectionState.set('lost');
      }
    };
    client.onStompError = () => {
      if (this.client === client) {
        this.connectionState.set('lost');
      }
    };
    this.client = client;
    client.activate();
  }

  disconnect(): void {
    const client = this.client;
    this.client = null;
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    this.subscriptions = [];
    void client?.deactivate();
  }

  /**
   * Ein unlesbarer Rahmen kostet sein Ereignis, nicht den Feed: das Abonnement
   * bleibt stehen und der naechste Test laeuft wieder ein.
   */
  private dispatch(projectId: string, message: IMessage): void {
    try {
      this.eventSubject.next({ projectId, entry: JSON.parse(message.body) as LivePipelineEntry });
    } catch {
      return;
    }
  }
}

function topicFor(projectId: string): string {
  return `/topic/projects/${projectId}/pipelines`;
}
